import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Eye, Trash2, AlertCircle, CheckCircle, Clock, Loader, Play, RotateCcw, Info, ChevronDown, ChevronUp, ArrowDown, ArrowUpDown, Scan, X, Briefcase, CreditCard, Coins, Lock } from 'lucide-react';
import JSZip from 'jszip';
import { caseApi, documentApi } from '../../services/api';
import { processingApi } from '../../services/processingApi';
import { GuidelinePicker, type UploadedGuidelineFile } from '../GuidelinePicker';
import { fetchSuggestedGuidelines, type SuggestedGuidelinesResponse } from '../../services/guidelineService';
import { creditApi, type CreditBalance } from '../../services/creditApi';
import { billingApi, type SubscriptionStatus } from '../../services/billingApi';
import { Document, LegacyCase, UploadProgress, InstructionLetterData } from '../../types';
import type { CreditShortfall } from '../../hooks/useMetadataExtraction';
import { PasswordProtectedDialog } from '../PasswordProtectedDialog';

function isDicomFile(file: File): boolean {
  const ext = file.name.toLowerCase();
  return ext.endsWith('.dcm') || ext.endsWith('.dicom');
}

function isDicomFilename(filename: string): boolean {
  const lower = filename.toLowerCase();
  return lower.endsWith('.dcm') || lower.endsWith('.dicom');
}

function isZipFile(file: File): boolean {
  const ext = file.name.toLowerCase();
  return ext.endsWith('.zip');
}

class EncryptedZipError extends Error {
  constructor(public readonly zipName: string) {
    super(`ZIP "${zipName}" is password-protected`);
    this.name = 'EncryptedZipError';
  }
}

function isEncryptedZipMessage(msg: unknown): boolean {
  return /encrypt|password/i.test(String((msg as { message?: string })?.message ?? msg ?? ''));
}

async function extractZipFiles(zipFile: File): Promise<File[]> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(zipFile);
  } catch (e) {
    if (isEncryptedZipMessage(e)) throw new EncryptedZipError(zipFile.name);
    throw e;
  }

  const extracted: File[] = [];
  const entries = Object.entries(zip.files);
  for (const [path, entry] of entries) {
    if (entry.dir) continue;
    // Skip hidden/system files
    const name = path.split('/').pop() || path;
    if (name.startsWith('.') || name.startsWith('__')) continue;
    let blob: Blob;
    try {
      blob = await entry.async('blob');
    } catch (e) {
      if (isEncryptedZipMessage(e)) throw new EncryptedZipError(zipFile.name);
      throw e;
    }
    const ext = name.toLowerCase().split('.').pop() || '';
    const mimeMap: Record<string, string> = {
      pdf: 'application/pdf', doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      txt: 'text/plain', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      png: 'image/png', gif: 'image/gif', tiff: 'image/tiff', tif: 'image/tiff',
      dcm: 'application/dicom', dicom: 'application/dicom',
    };
    const mime = mimeMap[ext] || 'application/octet-stream';
    extracted.push(new File([blob], name, { type: mime }));
  }
  return extracted;
}
import { formatDateTime } from '../../utils/dateFormat';

// ── Module-level upload state — survives component unmount during tab switches ──
const MAX_INDIVIDUAL_FILE_SIZE = 1024 * 1024 * 1024; // 1GB

const _uploadProgressByCase = new Map<string, UploadProgress[]>();
const _uploadListeners = new Set<() => void>();

function getUploadProgressForCase(caseId: string): UploadProgress[] {
  return _uploadProgressByCase.get(caseId) || [];
}

function setModuleUploadProgress(caseId: string, updater: UploadProgress[] | ((prev: UploadProgress[]) => UploadProgress[])) {
  const prev = getUploadProgressForCase(caseId);
  const next = typeof updater === 'function' ? updater(prev) : updater;
  if (next.length === 0) {
    _uploadProgressByCase.delete(caseId);
  } else {
    _uploadProgressByCase.set(caseId, next);
  }
  _uploadListeners.forEach(fn => fn());
}

function useUploadProgress(caseId: string): [UploadProgress[], (updater: UploadProgress[] | ((prev: UploadProgress[]) => UploadProgress[])) => void] {
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender(n => n + 1);
    _uploadListeners.add(listener);
    return () => { _uploadListeners.delete(listener); };
  }, []);
  const setter = useCallback(
    (updater: UploadProgress[] | ((prev: UploadProgress[]) => UploadProgress[])) => setModuleUploadProgress(caseId, updater),
    [caseId]
  );
  return [getUploadProgressForCase(caseId), setter];
}

const CASE_TYPE_LABELS: Record<string, string> = {
  MEDICAL_NEGLIGENCE: 'Medical Negligence',
  CLINICAL_REVIEW: 'Clinical Review',
  EXPERT_OPINION: 'Expert Opinion',
  TRIBUNAL: 'Tribunal',
  INQUEST: 'Inquest',
  PERSONAL_INJURIES: 'Personal Injuries',
  OTHER: 'Other',
  EXPLORATORY: 'Exploratory',
};

const GUIDANCE_TEMPLATES: Record<string, Array<{ id: string; label: string; template: string }>> = {
  MEDICAL_NEGLIGENCE: [
    { id: 'diagnosis_timeline', label: 'Diagnosis Timeline', template: 'Extract the diagnosis timeline and clinical decision points.\nPay attention to:\n- Initial symptoms and presentation\n- Diagnostic tests ordered and results\n- Differential diagnoses considered\n- Time to definitive diagnosis\n- Intervals between key events' },
    { id: 'treatment_sequence', label: 'Treatment Sequence', template: 'Extract the treatment sequence and clinical actions taken.\nPay attention to:\n- Treatment plan and rationale documented\n- Medication administration (drug, dose, route, timing)\n- Surgical interventions and technique\n- Monitoring and response to treatment\n- Documented protocol references' },
    { id: 'communication_chain', label: 'Communication Chain', template: 'Extract the communication chain between healthcare providers.\nPay attention to:\n- Handovers between shifts/teams\n- Referrals and consultations\n- Documentation and record-keeping\n- Patient/family communication records\n- Information flow and timing' },
    { id: 'monitoring_timeline', label: 'Monitoring Timeline', template: 'Extract the monitoring timeline and escalation events.\nPay attention to:\n- Vital signs and trends\n- Early warning scores (NEWS, PEWS, etc.)\n- Escalation events and timing\n- Response times documented\n- Frequency and type of observations' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  CLINICAL_REVIEW: [
    { id: 'care_pathway', label: 'Care Pathway', template: 'Extract the care pathway and clinical actions.\nPay attention to:\n- Clinical guidelines referenced\n- Investigations ordered and results\n- Timing of interventions\n- Multidisciplinary team involvement\n- Documented patient interactions' },
    { id: 'outcome_timeline', label: 'Outcome Timeline', template: 'Extract patient outcomes and contributing events.\nPay attention to:\n- Clinical outcomes (recovery, complications, mortality)\n- Events that preceded outcomes\n- Documented complications and timing\n- Quality indicators referenced' },
    { id: 'guideline_references', label: 'Guideline References', template: 'Extract references to clinical guidelines and documented compliance.\nPay attention to:\n- NICE guidelines referenced\n- Local protocols and policies cited\n- Documented deviations and stated rationale\n- Clinical reasoning recorded' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  EXPERT_OPINION: [
    { id: 'clinical_evidence', label: 'Clinical Evidence', template: 'Extract the clinical evidence relevant to the question posed.\nPay attention to:\n- Evidence directly relevant to the question\n- Documented clinical reasoning\n- Alternative approaches documented\n- Professional standards referenced' },
    { id: 'care_documentation', label: 'Care Documentation', template: 'Extract documentation of care and professional standards referenced.\nPay attention to:\n- Documented clinical decisions and rationale\n- Resources and constraints noted\n- Standards and guidelines cited\n- Communication records' },
    { id: 'event_sequence', label: 'Event Sequence', template: 'Extract the sequence of events and documented causal factors.\nPay attention to:\n- Temporal sequence of events\n- Documented contributing factors\n- Documented causal links\n- Conditional factors noted' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  TRIBUNAL: [
    { id: 'conduct_record', label: 'Conduct Record', template: 'Extract records of professional conduct and competence.\nPay attention to:\n- Clinical competence documentation\n- Professional behavior records\n- Patient safety incidents documented\n- Pattern and frequency of events' },
    { id: 'employment_record', label: 'Employment Record', template: 'Extract workplace conduct and performance documentation.\nPay attention to:\n- Performance records and documentation\n- Disciplinary process records\n- Workplace communication records\n- Policies and procedures referenced' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  INQUEST: [
    { id: 'event_timeline', label: 'Event Timeline', template: 'Extract the timeline of events leading to death.\nPay attention to:\n- Chronological sequence of events\n- Clinical decisions and interventions documented\n- System factors and resource issues noted\n- Documented alternative actions considered' },
    { id: 'systemic_factors', label: 'Systemic Factors', template: 'Extract systemic and process factors documented.\nPay attention to:\n- System issues or gaps documented\n- Process and procedural records\n- Training or resource issues noted\n- Recommendations documented' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  PERSONAL_INJURIES: [
    { id: 'injury_timeline', label: 'Injury Timeline', template: 'Extract the timeline of injuries, treatment, and recovery.\nPay attention to:\n- Mechanism and circumstances of injury\n- Initial presentation and assessment\n- Diagnostic findings (imaging, examinations)\n- Treatment provided and clinical rationale\n- Recovery trajectory and prognosis' },
    { id: 'functional_impact', label: 'Functional Impact', template: 'Extract evidence of functional impact and disability.\nPay attention to:\n- Pre-injury baseline function and activities\n- Post-injury functional limitations documented\n- Activities of daily living affected\n- Occupational and social impact\n- Psychological sequelae documented' },
    { id: 'causation_evidence', label: 'Causation Evidence', template: 'Extract evidence relevant to causation and prognosis.\nPay attention to:\n- Pre-existing conditions documented\n- Temporal relationship between injury and symptoms\n- Expert opinions on causation\n- Aggravation of pre-existing conditions\n- Prognosis and future care needs' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  OTHER: [
    { id: 'full_extraction', label: 'Full Extraction', template: 'Extract all medical events and documented information.\nPay attention to:\n- Complete timeline of care\n- All clinical decisions and documented rationale\n- Test results and investigations\n- Treatment and outcomes\n- Communication and documentation' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
  EXPLORATORY: [
    { id: 'full_extraction', label: 'Full Extraction', template: 'Extract all medical events and documented information.\nPay attention to:\n- Complete timeline of care\n- All clinical decisions and documented rationale\n- Test results and investigations\n- Treatment and outcomes\n- Communication and documentation' },
    { id: 'pattern_detection', label: 'Pattern Detection', template: 'Extract patterns, inconsistencies, and notable features.\nPay attention to:\n- Recurring issues or themes\n- Contradictions in documentation\n- Gaps in records or information\n- Unusual or unexpected events' },
    { id: 'custom', label: 'Custom', template: '' },
  ],
};

// Strip legacy focus-area prefix from stored guidance (migrates old data transparently)
function stripFocusAreaPrefix(guidance: string): string {
  const FOCUS_AREA_PREFIX = '%%FOCUS:';
  const FOCUS_AREA_SUFFIX = '%%\n';
  if (guidance.startsWith(FOCUS_AREA_PREFIX)) {
    const endIdx = guidance.indexOf(FOCUS_AREA_SUFFIX);
    if (endIdx !== -1) return guidance.slice(endIdx + FOCUS_AREA_SUFFIX.length);
  }
  return guidance;
}

interface DocumentsTabProps {
  caseId: string;
  caseData?: LegacyCase;
  onStartAnalysis?: () => void;
  hasMetadata?: boolean;
  analysisInProgress?: boolean;
  caseBriefOpen?: boolean | null;
  onCaseBriefToggle?: (open: boolean) => void;
  creditShortfall?: CreditShortfall | null;
  onDismissCreditShortfall?: () => void;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  caseId,
  caseData,
  onStartAnalysis,
  hasMetadata,
  analysisInProgress,
  caseBriefOpen: caseBriefOpenProp,
  onCaseBriefToggle,
  creditShortfall,
  onDismissCreditShortfall,
}) => {
  const [uploadProgress, setUploadProgress] = useUploadProgress(caseId);
  const [bundleComplete, setBundleComplete] = useState(false);
  const [uploadErrorsExpanded, setUploadErrorsExpanded] = useState(false);
  const [recordsUploadOpen, setRecordsUploadOpen] = useState(true);
  // Use lifted state from parent (survives tab switches), fall back to local default
  const constraintsOpen = caseBriefOpenProp ?? false;
  const setConstraintsOpen = (open: boolean) => onCaseBriefToggle?.(open);
  // Case framing choice — deliberate, no default. User must pick "brief" or
  // "records-only" before Start Analysis is enabled. Once chosen, no re-toggle.
  const [caseFraming, setCaseFraming] = useState<'brief' | 'records-only' | null>(null);
  const [incidentDateStart, setIncidentDateStart] = useState(caseData?.incidentDateStart || '');
  const [incidentDateEnd, setIncidentDateEnd] = useState(caseData?.incidentDateEnd || '');

  // Re-analysis confirmation dialog state
  const [showReanalysisConfirm, setShowReanalysisConfirm] = useState(false);
  const [reanalysisReason, setReanalysisReason] = useState('');

  // Start-analysis credit confirmation dialog state (first-time analysis)
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [recountingPages, setRecountingPages] = useState(false);

  // Password-protected PDF dialog state — opens when Start Analysis encounters locked files
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pendingAnalysisAction, setPendingAnalysisAction] = useState<'start' | 'reanalyse' | null>(null);

  // Password-protected ZIP upload error — shown when a dropped zip is encrypted
  const [encryptedZipNames, setEncryptedZipNames] = useState<string[]>([]);

  const REANALYSIS_REASONS = [
    'New documents added to the bundle',
    'Documents were removed or replaced',
    'Previous analysis had errors or missing data',
    'Case brief or guidance was updated',
    'Other',
  ];

  const [docSortField, setDocSortField] = useState<'filename' | 'fileSize' | 'pageCount' | 'processingStatus'>('filename');
  const [docSortDir, setDocSortDir] = useState<'asc' | 'desc'>('asc');
  const handleDocSort = (field: typeof docSortField) => {
    if (docSortField === field) {
      setDocSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setDocSortField(field);
      setDocSortDir('asc');
    }
  };

  const [customGuidanceText, setCustomGuidanceTextRaw] = useState(stripFocusAreaPrefix(caseData?.processingGuidance || ''));
  // Keep refs in sync so the debounced auto-save always reads the latest values
  const guidanceRef = useRef(customGuidanceText);
  const dateStartRef = useRef(incidentDateStart);
  const dateEndRef = useRef(incidentDateEnd);
  const setCustomGuidanceText = (text: string) => { guidanceRef.current = text; setCustomGuidanceTextRaw(text); };
  const setIncidentDateStartWrapped = (val: string) => { dateStartRef.current = val; setIncidentDateStart(val); };
  const setIncidentDateEndWrapped = (val: string) => { dateEndRef.current = val; setIncidentDateEnd(val); };
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const stashedCustomText = useRef(stripFocusAreaPrefix(caseData?.processingGuidance || ''));
  const [selectedCaseTypes, setSelectedCaseTypes] = useState<string[]>(caseData?.caseTypes || []);

  // Instruction letter state — supports multiple uploads
  const [instructionLetterItems, setInstructionLetterItems] = useState<Array<{
    docId: string;
    filename: string;
    data: InstructionLetterData | null;
    extracting: boolean;
    expanded: boolean;
  }>>(() => {
    if (caseData?.instructionLetterDocumentId) {
      const data = caseData.instructionLetterData
        ? (typeof caseData.instructionLetterData === 'string' ? JSON.parse(caseData.instructionLetterData) : caseData.instructionLetterData)
        : null;
      return [{ docId: caseData.instructionLetterDocumentId, filename: 'Instruction Letter', data, extracting: false, expanded: false }];
    }
    return [];
  });

  // Guidelines state
  const [selectedGuidelines, setSelectedGuidelines] = useState<string[]>((caseData as any)?.selectedGuidelines || []);
  const [suggestedData, setSuggestedData] = useState<SuggestedGuidelinesResponse | null>(null);
  const [uploadedGuidelineFiles, setUploadedGuidelineFiles] = useState<UploadedGuidelineFile[]>([]);
  const [isFindingRelevant, setIsFindingRelevant] = useState(false);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // If the case already has any brief data (case type, dates, instruction
  // letter, or processing guidance) the framing choice is implicitly "brief" —
  // locks the user out of "records-only" so they can't orphan the brief data.
  const hasBriefData =
    selectedCaseTypes.length > 0 ||
    instructionLetterItems.length > 0 ||
    !!incidentDateStart ||
    !!incidentDateEnd ||
    !!customGuidanceText.trim();
  const effectiveFraming: 'brief' | 'records-only' | null =
    hasBriefData ? 'brief' : caseFraming;

  // Sync local state when caseData changes (e.g. after save or refetch)
  useEffect(() => {
    if (caseData) {
      setIncidentDateStartWrapped(caseData.incidentDateStart || '');
      setIncidentDateEndWrapped(caseData.incidentDateEnd || '');
      setCustomGuidanceText(stripFocusAreaPrefix(caseData.processingGuidance || ''));
      if (caseData.instructionLetterDocumentId) {
        const parsed = caseData.instructionLetterData
          ? (typeof caseData.instructionLetterData === 'string' ? JSON.parse(caseData.instructionLetterData) : caseData.instructionLetterData)
          : null;
        setInstructionLetterItems(prev => {
          const idx = prev.findIndex(il => il.docId === caseData.instructionLetterDocumentId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], data: parsed || updated[idx].data };
            return updated;
          }
          return prev;
        });
      }
      if (caseData.caseTypes) {
        setSelectedCaseTypes(caseData.caseTypes);
      }
    }
  }, [caseData?.incidentDateStart, caseData?.incidentDateEnd, caseData?.processingGuidance, caseData?.instructionLetterData, caseData?.instructionLetterDocumentId, caseData?.caseTypes]);

  const primaryCaseType = selectedCaseTypes[0] || 'EXPLORATORY';
  const availableTemplates = GUIDANCE_TEMPLATES[primaryCaseType] || GUIDANCE_TEMPLATES.EXPLORATORY;

  const handleCaseTypeToggle = useCallback(async (typeValue: string) => {
    // Single-select: clicking the active type deselects it, otherwise switch to the new one
    const newTypes = selectedCaseTypes.includes(typeValue) ? [] : [typeValue];
    setSelectedCaseTypes(newTypes);
    try {
      await caseApi.updateCase(caseId, { caseTypes: newTypes } as any);
      queryClient.invalidateQueries(['case', caseId]);
    } catch (error) {
      console.error('Failed to save case types:', error);
      setSelectedCaseTypes(selectedCaseTypes);
    }
  }, [caseId, selectedCaseTypes, queryClient]);

  // Auto-save constraints after 1.5s of inactivity.
  // Reads from refs so the debounced callback always saves the latest values,
  // even when React hasn't re-rendered yet after a setState call.
  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await caseApi.updateCase(caseId, {
          incidentDateStart: dateStartRef.current || undefined,
          incidentDateEnd: dateEndRef.current || undefined,
          processingGuidance: guidanceRef.current || undefined,
        });
        queryClient.invalidateQueries(['case', caseId]);
        } catch (error) {
        console.error('Failed to auto-save constraints:', error);
      }
    }, 1500);
  }, [caseId, queryClient]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, []);

  const handleTemplateSelect = (templateId: string) => {
    const template = availableTemplates.find(t => t.id === templateId);
    // Stash current text when leaving Custom so we can restore it
    if (selectedTemplate === 'custom') {
      stashedCustomText.current = customGuidanceText;
    }
    setSelectedTemplate(templateId);
    if (templateId === 'custom') {
      setCustomGuidanceText(stashedCustomText.current);
    } else if (template && template.template) {
      setCustomGuidanceText(template.template);
    }
    scheduleAutoSave();
  };

  // Guidelines: find relevant using AI
  const handleFindRelevantGuidelines = useCallback(async () => {
    setIsFindingRelevant(true);
    try {
      const instructionSummary = instructionLetterItems
        .filter(il => il.data)
        .map(il => JSON.stringify(il.data))
        .join('\n') || undefined;
      const data = await fetchSuggestedGuidelines(
        caseId,
        caseData?.description || '',
        selectedCaseTypes,
        instructionSummary,
      );
      setSuggestedData(data);
    } catch (e) {
      console.error('Failed to fetch suggested guidelines:', e);
    } finally {
      setIsFindingRelevant(false);
    }
  }, [caseId, caseData?.description, selectedCaseTypes, instructionLetterItems]);

  // Guidelines: save selected guidelines to case
  const handleGuidelinesChange = useCallback(async (guidelineIds: string[]) => {
    setSelectedGuidelines(guidelineIds);
    try {
      await caseApi.updateCase(caseId, { selectedGuidelines: guidelineIds } as any);
      queryClient.invalidateQueries(['case', caseId]);
    } catch (error) {
      console.error('Failed to save guidelines:', error);
    }
  }, [caseId, queryClient]);

  // Guidelines: upload custom guideline PDFs
  const handleUploadGuideline = useCallback(async (files: File[]) => {
    try {
      const result = await documentApi.uploadDocuments(caseId, files, undefined, 'GUIDELINE');
      if (Array.isArray(result)) {
        const newFiles: UploadedGuidelineFile[] = result
          .filter((r: any) => r.status !== 'ERROR' && r.documentId)
          .map((r: any) => ({ id: r.documentId, name: r.fileName || r.originalFileName || 'Guideline' }));
        setUploadedGuidelineFiles(prev => [...prev, ...newFiles]);
      }
    } catch (e) {
      console.error('Failed to upload guideline file:', e);
    }
  }, [caseId]);

  const handleRemoveUploadedGuideline = useCallback((id: string) => {
    setUploadedGuidelineFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const { data: documentsData, isLoading, error } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    {
      refetchInterval: (data) => {
        const docs = data || [];
        const hasProcessing = docs.some(d =>
          ['uploading', 'queued', 'processing'].includes(d.processingStatus?.toLowerCase())
        );
        // Refetch while any document is missing a page count (async extraction may still be running)
        const needsPageCounts = docs.some(d => !d.pageCount);
        return (hasProcessing || needsPageCounts) ? 3000 : false;
      },
    }
  );

  const allDocuments = documentsData || [];
  const documents = allDocuments.filter((doc: any) => doc.documentType !== 'INSTRUCTION_LETTER');

  const processingStatuses = ['uploading', 'queued', 'processing'];
  const hasProcessingDocs = documents.some(d => processingStatuses.includes(d.processingStatus?.toLowerCase()));
  const hasActiveUploads = uploadProgress.some(p => p.status === 'uploading' || p.status === 'processing');
  const documentsStillProcessing = hasProcessingDocs || hasActiveUploads;
  const processingDocCount = documents.filter(d => processingStatuses.includes(d.processingStatus?.toLowerCase())).length;

  const sortedDocuments = [...documents].sort((a, b) => {
    const dir = docSortDir === 'asc' ? 1 : -1;
    switch (docSortField) {
      case 'filename':
        return dir * (a.filename || '').localeCompare(b.filename || '');
      case 'fileSize':
        return dir * ((a.fileSize || 0) - (b.fileSize || 0));
      case 'pageCount':
        return dir * ((a.pageCount || 0) - (b.pageCount || 0));
      case 'processingStatus':
        return dir * (getStatusLabel(a.processingStatus)).localeCompare(getStatusLabel(b.processingStatus));
      default:
        return 0;
    }
  });

  // Sync instruction letter items from documents query (discovers letters from previous sessions)
  useEffect(() => {
    if (!documentsData) return;
    const ilDocs = documentsData.filter((doc: any) => doc.documentType === 'INSTRUCTION_LETTER');
    if (ilDocs.length === 0) return;
    setInstructionLetterItems(prev => {
      const existingDocIds = new Set(prev.map(il => il.docId));
      const newItems = ilDocs
        .filter((doc: any) => !existingDocIds.has(doc.documentId))
        .map((doc: any) => ({
          docId: doc.documentId,
          filename: doc.filename || 'Instruction Letter',
          data: null,
          extracting: false,
          expanded: false,
        }));
      // Update filenames for existing items (initial state from caseData lacks filenames)
      const updated = prev.map(il => {
        const doc = ilDocs.find((d: any) => d.documentId === il.docId);
        return doc ? { ...il, filename: doc.filename || il.filename } : il;
      });
      return newItems.length > 0 ? [...updated, ...newItems] : updated;
    });
  }, [documentsData]);

  const uploadMutation = useMutation(
    (files: File[]) => documentApi.uploadDocuments(caseId, files, (progress) => {
      setModuleUploadProgress(caseId, prev =>
        prev.map(p => {
          // Don't touch DICOM items — they have their own mutation
          if (isDicomFilename(p.filename)) return p;
          if (progress >= 90 && p.status === 'uploading') {
            return { ...p, progress: 90, status: 'processing' as const };
          }
          // Only move progress forward — never backward
          if (progress <= p.progress) return p;
          return { ...p, progress };
        })
      );
    }).then(result => {
      // In promise chain so it fires even if component is unmounted
      queryClient.invalidateQueries(['documents', caseId]);
      queryClient.invalidateQueries(['case', caseId]);

      // Check for per-file errors in partial-failure (207) responses
      const fileErrors: Record<string, string> = {};
      if (Array.isArray(result)) {
        result.forEach((r: any) => {
          if (r.status === 'ERROR' && r.filename) {
            fileErrors[r.filename] = r.message || 'Upload failed';
          }
        });
      }

      setModuleUploadProgress(caseId, prev =>
        prev
          .map(p => {
            if (isDicomFilename(p.filename)) return p;
            if (fileErrors[p.filename]) {
              return { ...p, status: 'error' as const, error: fileErrors[p.filename] };
            }
            return null; // mark successful items for removal
          })
          .filter((p): p is NonNullable<typeof p> => p !== null)
      );
      return result;
    }).catch(error => {
      const message = error?.message?.includes('413') || error?.message?.includes('too large') || error?.message?.includes('size exceeded')
        ? 'Upload too large. Individual files must be under 50MB.'
        : error?.message || 'Upload failed';
      setModuleUploadProgress(caseId, prev =>
        prev.map(p => {
          // Don't touch DICOM items — they have their own mutation
          if (isDicomFilename(p.filename)) return p;
          return p.status !== 'error' ? { ...p, status: 'error' as const, error: message } : p;
        })
      );
      throw error;
    }),
  );

  const deleteMutation = useMutation(documentApi.deleteDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries(['documents', caseId]);
      queryClient.invalidateQueries(['case', caseId]);
    }
  });

  const dicomUploadMutation = useMutation(
    (files: File[]) => documentApi.uploadDicomStudy(caseId, files, (progress) => {
      setModuleUploadProgress(caseId, prev =>
        prev.map(p => {
          if (!isDicomFilename(p.filename)) return p;
          if (progress >= 90 && p.status === 'uploading') {
            return { ...p, progress: 90, status: 'processing' as const };
          }
          // Only move progress forward — never backward
          if (progress <= p.progress) return p;
          return { ...p, progress };
        })
      );
    }).then(result => {
      queryClient.invalidateQueries(['documents', caseId]);
      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['dicom-studies', caseId]);
      setModuleUploadProgress(caseId, prev => prev.filter(p => !isDicomFilename(p.filename)));
      return result;
    }).catch(error => {
      // Upload may have succeeded server-side even if response parsing failed —
      // invalidate so the next fetch discovers any studies that were created.
      queryClient.invalidateQueries(['dicom-studies', caseId]);
      setModuleUploadProgress(caseId, prev =>
        prev.map(p => {
          if (!isDicomFilename(p.filename)) return p;
          return { ...p, status: 'error' as const, error: 'DICOM upload failed' };
        })
      );
      throw error;
    }),
  );

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Extract ZIP files client-side before uploading
    const zipFiles = acceptedFiles.filter(isZipFile);
    const nonZipFiles = acceptedFiles.filter(f => !isZipFile(f));

    let allFiles = [...nonZipFiles];
    const failedZips: { name: string; error: string }[] = [];
    const encryptedZips: string[] = [];
    for (const zip of zipFiles) {
      try {
        const extracted = await extractZipFiles(zip);
        allFiles = [...allFiles, ...extracted];
      } catch (err) {
        if (err instanceof EncryptedZipError) {
          encryptedZips.push(err.zipName);
        } else {
          console.error(`Failed to extract ZIP ${zip.name}:`, err);
          failedZips.push({ name: zip.name, error: 'Corrupted or invalid ZIP file' });
        }
      }
    }
    if (encryptedZips.length > 0) {
      setEncryptedZipNames(prev => [...prev, ...encryptedZips]);
    }

    // Validate individual file sizes (50MB per file)
    const oversizedFiles = allFiles.filter(f => f.size > MAX_INDIVIDUAL_FILE_SIZE);
    const validFiles = allFiles.filter(f => f.size <= MAX_INDIVIDUAL_FILE_SIZE);

    const dicomFiles = validFiles.filter(isDicomFile);
    const regularFiles = validFiles.filter(f => !isDicomFile(f));

    // Build initial progress — mark oversized files as errors immediately
    const initialProgress: UploadProgress[] = [
      ...validFiles.map(file => ({
        filename: file.name,
        progress: 0,
        status: 'uploading' as const,
      })),
      ...oversizedFiles.map(file => ({
        filename: file.name,
        progress: 0,
        status: 'error' as const,
        error: `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB, max 1GB)`,
      })),
      ...failedZips.map(z => ({
        filename: z.name,
        progress: 0,
        status: 'error' as const,
        error: z.error,
      })),
    ];
    setUploadProgress(initialProgress);

    if (regularFiles.length > 0) {
      uploadMutation.mutate(regularFiles);
    }
    if (dicomFiles.length > 0) {
      dicomUploadMutation.mutate(dicomFiles);
    }
  }, [uploadMutation, dicomUploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // Use a single wildcard MIME key so the OS file browser doesn't grey out
      // files based on unreliable MIME detection. Extensions do the real filtering.
      'application/*': ['.pdf', '.doc', '.docx', '.rtf', '.odt', '.dcm', '.dicom', '.zip'],
      'text/*': ['.txt', '.rtf'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.tif'],
    },
    maxSize: 2 * 1024 * 1024 * 1024,  // 2GB per case upload
    multiple: true,
    onDropRejected: (fileRejections) => {
      const oversized = fileRejections.filter(r =>
        r.errors.some(e => e.code === 'file-too-large')
      );
      const wrongType = fileRejections.filter(r =>
        r.errors.some(e => e.code === 'file-invalid-type')
      );

      const messages: string[] = [];
      if (oversized.length > 0) {
        messages.push(`${oversized.length} file(s) exceed the 1GB limit per upload`);
      }
      if (wrongType.length > 0) {
        messages.push(`${wrongType.length} file(s) have unsupported formats`);
      }

      setUploadProgress(fileRejections.map(r => ({
        filename: r.file.name,
        progress: 0,
        status: 'error' as const,
        error: r.errors[0]?.code === 'file-too-large'
          ? `File too large (${(r.file.size / 1024 / 1024).toFixed(1)}MB, max 1GB per upload)`
          : 'Unsupported file type'
      })));
    }
  });

  // Ask the backend to recount pages for any PDF/image docs where pageCount is
  // missing. The initial async extraction on upload can fail silently, so we
  // retry here before showing the credit dialog so the user sees accurate totals.
  const recountMissingPages = useCallback(async () => {
    const hasMissing = documents.some(d => !d.pageCount || d.pageCount === 0);
    if (!hasMissing) return;
    setRecountingPages(true);
    try {
      await documentApi.recountCaseDocumentPages(caseId);
      await queryClient.invalidateQueries(['documents', caseId]);
    } catch (e) {
      console.error('Failed to recount missing page counts:', e);
    } finally {
      setRecountingPages(false);
    }
  }, [caseId, documents, queryClient]);

  const lockedDocuments = documents.filter(d => d.processingStatus === 'password_protected');

  const handleStartAnalysis = () => {
    // Open the dialog immediately so the user gets instant feedback. The credit
    // summary shows a "counting pages…" state while the recount runs in the
    // background, and the Confirm button stays disabled until it finishes.
    // Late encryption detection (rare safety-net case) is handled at confirm time.
    if (hasMetadata) {
      setReanalysisReason('');
      setShowReanalysisConfirm(true);
    } else {
      setShowStartConfirm(true);
    }
    recountMissingPages();
  };

  const handlePasswordDialogContinue = () => {
    setShowPasswordDialog(false);
    const action = pendingAnalysisAction;
    setPendingAnalysisAction(null);
    // Re-enter the normal flow so the credit confirmation still runs.
    if (action === 'reanalyse') {
      setReanalysisReason('');
      setShowReanalysisConfirm(true);
      recountMissingPages();
    } else {
      setShowStartConfirm(true);
      recountMissingPages();
    }
  };

  // Late encryption check: the recount also re-detects encryption on documents
  // that slipped past the upload-time check. If anything is locked at confirm
  // time, swap to the password dialog instead of starting.
  const swapToPasswordDialogIfLocked = (action: 'start' | 'reanalyse'): boolean => {
    const refreshed = queryClient.getQueryData<Document[]>(['documents', caseId]) ?? documents;
    const locked = refreshed.filter(d => d.processingStatus === 'password_protected');
    if (locked.length === 0) return false;
    setShowStartConfirm(false);
    setShowReanalysisConfirm(false);
    setPendingAnalysisAction(action);
    setShowPasswordDialog(true);
    return true;
  };

  const handleConfirmStart = () => {
    if (swapToPasswordDialogIfLocked('start')) return;
    setShowStartConfirm(false);
    setRecordsUploadOpen(false);
    onStartAnalysis?.();
  };

  const handleConfirmReanalysis = () => {
    if (swapToPasswordDialogIfLocked('reanalyse')) return;
    setShowReanalysisConfirm(false);
    setRecordsUploadOpen(false);
    onStartAnalysis?.();
  };

  // Fetch fresh credit balance and subscription whenever a confirmation dialog is open.
  const confirmOpen = showStartConfirm || showReanalysisConfirm;
  const { data: creditBalance } = useQuery<CreditBalance>(
    ['credit-balance'],
    () => creditApi.getBalance(),
    { enabled: confirmOpen, refetchOnWindowFocus: false, staleTime: 0 },
  );
  const { data: subscriptionStatus } = useQuery<SubscriptionStatus | null>(
    ['subscription-status'],
    () => billingApi.getSubscription().catch(() => null),
    { enabled: confirmOpen, refetchOnWindowFocus: false, staleTime: 60_000 },
  );

  const processInstructionLetterFile = useCallback(async (file: File) => {
    if (instructionLetterItems.length > 0) {
      return;
    }
    // 1GB limit for instruction letters
    if (file.size > 1024 * 1024 * 1024) {
      alert(`"${file.name}" exceeds the 1GB limit for instruction letters.`);
      return;
    }

    const isFirstLetter = true;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setInstructionLetterItems(prev => [...prev, {
      docId: tempId, filename: file.name, data: null, extracting: true, expanded: false,
    }]);

    let currentId = tempId;
    try {
      // 1. Upload the file as a document (tagged as instruction letter so it's excluded from analysis)
      const uploadResult = await documentApi.uploadDocuments(caseId, [file], undefined, 'INSTRUCTION_LETTER');
      const docId = uploadResult?.[0]?.documentId;
      if (!docId) throw new Error('Upload returned no document ID');
      currentId = docId;
      setInstructionLetterItems(prev => prev.map(il => il.docId === tempId ? { ...il, docId } : il));

      // 2. Extract text from the file (skip billing — instruction letters are free)
      const text = await processingApi.extractTextFromFile(file, true);

      if (!text) throw new Error('Could not extract text from instruction letter');

      // 3. Extract structured data via processing service
      const extracted = await processingApi.extractInstructionLetter(text, file.name);
      setInstructionLetterItems(prev => prev.map(il => il.docId === docId ? { ...il, data: extracted, extracting: false } : il));

      // 4. Auto-populate from first instruction letter only
      if (isFirstLetter) {
        const guidanceParts: string[] = [];
        if (extracted.caseSummary) guidanceParts.push(extracted.caseSummary);
        if (extracted.questionsToAnswer?.length) {
          guidanceParts.push('\nKey questions:\n' + extracted.questionsToAnswer.map(q => `${q.number}. ${q.summary}`).join('\n'));
        }
        const newGuidanceText = guidanceParts.length > 0 ? guidanceParts.join('\n') : customGuidanceText;

        if (guidanceParts.length > 0) {
          setCustomGuidanceText(newGuidanceText);
          stashedCustomText.current = newGuidanceText;
          setSelectedTemplate('custom');
        }

        const claimant = extracted.parties?.claimant?.trim();
        const defendant = extracted.parties?.defendant?.trim();
        const autoName = (claimant && defendant) ? `${claimant} v ${defendant}` : claimant || defendant || undefined;
        const nameUpdate = (autoName && caseData?.name === 'Untitled Case') ? { name: autoName } : {};
        const patientUpdate = (claimant && (!caseData?.patientName || caseData.patientName === 'Unknown'))
          ? { patientName: claimant } : {};

        let caseTypesUpdate: { caseTypes?: string[] } = {};
        if (selectedCaseTypes.length === 0) {
          const reportType = (extracted.reportType || '').toLowerCase();
          let inferredType = 'MEDICAL_NEGLIGENCE';
          if (reportType.includes('inquest')) inferredType = 'INQUEST';
          else if (reportType.includes('tribunal')) inferredType = 'TRIBUNAL';
          else if (reportType.includes('expert')) inferredType = 'EXPERT_OPINION';
          else if (reportType.includes('clinical review')) inferredType = 'CLINICAL_REVIEW';
          else if (reportType.includes('personal injur')) inferredType = 'PERSONAL_INJURIES';
          setSelectedCaseTypes([inferredType]);
          caseTypesUpdate = { caseTypes: [inferredType] };
        }

        await caseApi.updateCase(caseId, {
          ...nameUpdate, ...patientUpdate, ...caseTypesUpdate,
          instructionLetterData: extracted,
          instructionLetterDocumentId: docId,
          processingGuidance: newGuidanceText,
        } as any);
      }

      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['documents', caseId]);
    } catch (error) {
      console.error(`Instruction letter extraction failed for ${file.name}:`, error);
      setInstructionLetterItems(prev => prev.filter(il => il.docId !== currentId));
    }
  }, [caseId, queryClient, customGuidanceText, instructionLetterItems.length, selectedCaseTypes, caseData?.name, caseData?.patientName]);

  const handleInstructionLetterUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processInstructionLetterFile(file);
    e.target.value = '';
  }, [processInstructionLetterFile]);

  const [ilDragOver, setIlDragOver] = useState(false);

  const handleIlDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIlDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const accepted = ['.pdf', '.docx', '.doc', '.txt', '.rtf', '.odt'];
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!accepted.includes(ext)) {
      alert('Please upload a PDF, DOCX, DOC, TXT, RTF, or ODT file.');
      return;
    }
    await processInstructionLetterFile(file);
  }, [processInstructionLetterFile]);

  const handleRemoveInstructionLetter = useCallback(async (docId: string) => {
    try {
      await documentApi.deleteDocument(docId);
    } catch (error) {
      console.error('Failed to delete instruction letter document:', error);
    }
    setInstructionLetterItems(prev => prev.filter(il => il.docId !== docId));
    // If removing the primary letter, clear case-level data
    if (docId === caseData?.instructionLetterDocumentId) {
      await caseApi.updateCase(caseId, {
        instructionLetterData: '',
        instructionLetterDocumentId: '',
      } as any);
    }
    queryClient.invalidateQueries(['case', caseId]);
    queryClient.invalidateQueries(['documents', caseId]);
  }, [caseId, caseData?.instructionLetterDocumentId, queryClient]);

  const getDocumentIcon = (doc: Document) => {
    if (doc.documentType === 'dicom_image') {
      return <Scan className="h-4 w-4 text-cyan-600" />;
    }
    switch (doc.documentType) {
      case 'clinic_letter':
      case 'correspondence':
        return '📄';
      case 'discharge_summary':
        return '📋';
      case 'nursing_notes':
        return '📝';
      case 'investigation_report':
        return '🔬';
      case 'administrative':
      case 'legal_document':
        return '📁';
      case 'expert_report':
      case 'witness_statement':
        return '⚖️';
      case 'patient_record':
        return '🏥';
      case 'guideline':
      case 'academic_paper':
        return '📚';
      default:
        return '📄';
    }
  };

  const getProcessingStatusIcon = (status: Document['processingStatus']) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'uploaded':
      case 'readyforprocessing':
      case 'ready':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'uploading':
      case 'processing':
        return <Loader className="h-4 w-4 text-cyan-500 animate-spin" />;
      case 'queued':
        return <Clock className="h-4 w-4 text-cyan-500" />;
      case 'password_protected':
        return <Lock className="h-4 w-4 text-amber-500" />;
      case 'skipped_by_user':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getStatusLabel = (status: Document['processingStatus']) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'uploaded':
      case 'readyforprocessing':
      case 'ready':
        return 'Uploaded';
      case 'uploading':
        return 'Uploading...';
      case 'queued':
        return 'Queued';
      case 'password_protected':
        return 'Password required';
      case 'skipped_by_user':
        return 'Excluded from analysis';
      case 'processing':
        return 'Processing...';
      case 'processed':
        return 'Processed';
      case 'error':
      case 'failed':
        return 'Error';
      default:
        return 'Uploaded';
    }
  };

  const getStatusTooltip = (status: Document['processingStatus']): string => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'uploaded':
      case 'readyforprocessing':
      case 'ready':
        return 'Uploaded and ready for analysis';
      case 'uploading':
        return 'Uploading to server';
      case 'queued':
        return 'Queued for processing';
      case 'password_protected':
        return 'Password-protected \u2014 unlock required before analysis';
      case 'skipped_by_user':
        return 'You chose to exclude this file from analysis';
      case 'processing':
        return 'Extracting text and running OCR';
      case 'processed':
        return 'Text extracted, ready for analysis';
      case 'error':
      case 'failed':
        return 'Processing failed \u2014 try re-uploading';
      default:
        return 'Uploaded';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes <= 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDocumentType = (type: string | undefined | null) => {
    const labels: Record<string, string> = {
      'clinic_letter': 'Clinic Letter',
      'discharge_summary': 'Discharge Summary',
      'nursing_notes': 'Nursing Notes',
      'investigation_report': 'Investigation Report',
      'administrative': 'Administrative',
      'medical_report': 'Medical Report',
      'lab_results': 'Lab Results',
      'imaging_report': 'Imaging Report',
      'patient_record': 'Patient Record',
      'guideline': 'Guideline',
      'academic_paper': 'Academic Paper',
      'correspondence': 'Correspondence',
      'legal_document': 'Legal Document',
      'instruction_letter': 'Instruction Letter',
      'dicom_image': 'DICOM Image',
      'expert_report': 'Expert Report',
      'witness_statement': 'Witness Statement',
      'other': 'Other',
      'unclassified': 'Pending Classification',
      'unknown': 'Pending Classification',
    };
    if (!type) return 'Pending Classification';
    return labels[type.toLowerCase()] || type.replace(/_/g, ' ');
  };

  // ── Credit confirmation maths ─────────────────────────────────────────
  // 1 page credit is deducted per analysed page. Instruction letters are
  // already excluded from `documents`. Pages not yet counted contribute 0
  // but we flag that total as approximate.
  const pagesWithCounts = documents.filter(d => (d.pageCount || 0) > 0).length;
  const pagesPending = documents.length - pagesWithCounts;
  const creditsRequired = documents.reduce((sum, d) => sum + (d.pageCount || 0), 0);
  const freePageBalance = creditBalance?.freePageBalance ?? 0;
  const hasSubscription = creditBalance?.hasActiveSubscription ?? false;
  const freeAfter = Math.max(0, freePageBalance - creditsRequired);
  const paidPages = Math.max(0, creditsRequired - freePageBalance);
  const currentRate = creditBalance?.currentRatePence ?? 30;
  const estimatedCostPence = paidPages * currentRate;
  const formatPence = (p: number) =>
    p >= 100 ? `\u00A3${(p / 100).toFixed(2)}` : `${p}p`;

  // Subscribers can always proceed (consumption billing).
  // Non-subscribers need enough free pages.
  const hasShortfall = !hasSubscription && freePageBalance < creditsRequired;

  const renderCreditSummary = () => (
    <div className="rounded-lg border border-medical-200 dark:border-medical-700 bg-medical-50 dark:bg-medical-900/40 p-4 space-y-2 text-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-medical-500 dark:text-medical-400">
        <Coins className="h-3.5 w-3.5" />
        Credit summary
      </div>
      {!creditBalance ? (
        <div className="flex items-center gap-2 text-medical-500 dark:text-medical-400">
          <Loader className="h-3.5 w-3.5 animate-spin" />
          <span>Loading balance…</span>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-medical-600 dark:text-medical-300">
              Credits to be deducted
              {recountingPages ? (
                <span className="ml-1 text-xs text-medical-400 dark:text-medical-500 inline-flex items-center gap-1">
                  <Loader className="h-3 w-3 animate-spin" />
                  counting pages…
                </span>
              ) : pagesPending > 0 && (
                <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                  (approx. — {pagesPending} {pagesPending === 1 ? 'document has' : 'documents have'} no page count recorded)
                </span>
              )}
            </span>
            <span className="font-semibold text-medical-900 dark:text-medical-100 tabular-nums">
              {creditsRequired} {creditsRequired === 1 ? 'page' : 'pages'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-medical-600 dark:text-medical-300">Free pages available</span>
            <span className="font-semibold text-medical-900 dark:text-medical-100 tabular-nums">
              {freePageBalance} {freePageBalance === 1 ? 'page' : 'pages'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-medical-600 dark:text-medical-300">Free pages remaining after</span>
            <span className="font-semibold text-medical-900 dark:text-medical-100 tabular-nums">
              {freeAfter} {freeAfter === 1 ? 'page' : 'pages'}
            </span>
          </div>
          {paidPages > 0 && hasSubscription && (
            <div className="pt-2 mt-2 border-t border-medical-200 dark:border-medical-700">
              <div className="flex items-center justify-between">
                <span className="text-medical-600 dark:text-medical-300">
                  Consumption ({paidPages} pages at {currentRate}p)
                </span>
                <span className="font-semibold text-orange-600 dark:text-orange-400 tabular-nums">
                  ~{formatPence(estimatedCostPence)}
                </span>
              </div>
              <p className="text-[10px] text-medical-400 dark:text-medical-500 mt-1">
                Actual cost depends on your monthly volume tier. Invoiced per case on completion.
              </p>
            </div>
          )}
          {hasShortfall && (
            <div className="pt-2 mt-2 border-t border-medical-200 dark:border-medical-700">
              <p className="text-xs text-red-600 dark:text-red-400">
                Not enough free pages. Subscribe to continue processing.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Start Analysis Credit Confirmation Dialog */}
      {showStartConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowStartConfirm(false)}>
          <div className="bg-white dark:bg-medical-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-full flex-shrink-0">
                <Play className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Start analysis?</h3>
                <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
                  Please confirm the credits that will be deducted from your account before we begin.
                </p>
              </div>
            </div>

            <div className="mb-5">{renderCreditSummary()}</div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowStartConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-medical-700 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStart}
                disabled={!creditBalance || hasShortfall || recountingPages}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  creditBalance && !hasShortfall && !recountingPages
                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                    : 'bg-gray-200 dark:bg-medical-700 text-gray-400 dark:text-medical-500 cursor-not-allowed'
                }`}
              >
                Confirm &amp; Start Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-analysis Confirmation Dialog */}
      {showReanalysisConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowReanalysisConfirm(false)}>
          <div className="bg-white dark:bg-medical-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Re-run analysis?</h3>
                <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
                  This will replace all existing analysis results, including the chronology, gap analysis, inconsistencies, and any other extracted data.
                </p>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">
                Why are you re-running the analysis?
              </label>
              <div className="space-y-2">
                {REANALYSIS_REASONS.map((reason) => (
                  <label key={reason} className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="radio"
                      name="reanalysis-reason"
                      value={reason}
                      checked={reanalysisReason === reason}
                      onChange={() => setReanalysisReason(reason)}
                      className="h-4 w-4 text-primary-600 border-medical-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-medical-700 dark:text-medical-300 group-hover:text-medical-900 dark:group-hover:text-medical-100">
                      {reason}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-5">{renderCreditSummary()}</div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowReanalysisConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-medical-700 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReanalysis}
                disabled={!reanalysisReason || !creditBalance || hasShortfall || recountingPages}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  reanalysisReason && creditBalance && !hasShortfall && !recountingPages
                    ? 'bg-amber-600 text-white hover:bg-amber-700 shadow-sm'
                    : 'bg-gray-200 dark:bg-medical-700 text-gray-400 dark:text-medical-500 cursor-not-allowed'
                }`}
              >
                Re-run Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      <PasswordProtectedDialog
        open={showPasswordDialog}
        caseId={caseId}
        lockedDocuments={lockedDocuments}
        onClose={() => {
          setShowPasswordDialog(false);
          setPendingAnalysisAction(null);
        }}
        onContinue={handlePasswordDialogContinue}
        onDocumentsChanged={() => {
          queryClient.invalidateQueries(['documents', caseId]);
        }}
      />

      {encryptedZipNames.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setEncryptedZipNames([])}
        >
          <div
            className="bg-white dark:bg-medical-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">
                  Password-protected ZIP
                </h3>
                <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
                  We can't open encrypted ZIP archives in the browser.
                </p>
              </div>
            </div>

            <div className="mb-4 text-sm text-medical-700 dark:text-medical-300">
              <p className="mb-2">The following archive{encryptedZipNames.length === 1 ? ' is' : 's are'} password-protected:</p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-40 overflow-y-auto">
                {encryptedZipNames.map(name => (
                  <li key={name} className="truncate">{name}</li>
                ))}
              </ul>
            </div>

            <div className="mb-5 text-sm text-medical-700 dark:text-medical-300 bg-medical-50 dark:bg-medical-900/40 border border-medical-200 dark:border-medical-700 rounded-lg p-3">
              <p className="font-medium mb-1">What to do</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Unzip the archive on your computer using its password.</li>
                <li>Drag and drop the unzipped files into this case.</li>
              </ol>
            </div>

            <div className="flex items-center justify-end">
              <button
                onClick={() => setEncryptedZipNames([])}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 h-full overflow-auto" data-testid="documents-tab-content">
        {/* Credit shortfall — shown when user tries to analyse but lacks credits */}
        {creditShortfall && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                  Not enough page credits
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  This case requires <strong>{creditShortfall.required}</strong> pages but you only have <strong>{creditShortfall.balance}</strong> remaining.
                  Please top up your credits or subscribe to a plan to continue.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Link
                    to="/account"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    View plans &amp; billing
                  </Link>
                  <button
                    onClick={onDismissCreditShortfall}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All controls below are disabled while analysis is running */}
        <div className={analysisInProgress ? 'opacity-50 pointer-events-none select-none' : ''}>
        {/* Upload + Document Directory Row — collapsible */}
        <div className="mb-3 border border-medical-200 dark:border-medical-700 rounded-lg bg-white dark:bg-medical-800 overflow-hidden">
          <button
            onClick={() => setRecordsUploadOpen(!recordsUploadOpen)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors"
          >
            <h2 className="text-sm font-semibold text-medical-900 dark:text-medical-100 flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary-500" />
              Records Upload
              {documents.length > 0 && (
                <span className="text-xs font-normal text-medical-500 dark:text-medical-400">
                  ({documents.length} document{documents.length !== 1 ? 's' : ''})
                </span>
              )}
            </h2>
            <ChevronDown className={`w-4 h-4 text-medical-400 transition-transform duration-200 ${recordsUploadOpen ? '' : '-rotate-90'}`} />
          </button>
          {recordsUploadOpen && (
        <div className="px-4 pb-4 flex flex-col lg:flex-row lg:items-stretch gap-3">
          {/* Left: Upload Dropzone */}
          <div className="w-full lg:w-[35%] flex flex-col flex-shrink-0">
            <div
              {...getRootProps()}
              className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer flex flex-col items-center justify-center ${
                isDragActive
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-primary-300 bg-primary-50/40 dark:border-primary-700 dark:bg-primary-900/10 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
              }`}
              data-testid="upload-area"
            >
              <input {...getInputProps()} data-testid="file-input" />
              <Upload className="h-8 w-8 text-primary-400 dark:text-primary-500 mb-2" />
              <span className="font-medium text-sm text-medical-900 dark:text-medical-100 mb-0.5">
                {isDragActive ? 'Drop patient records here' : 'Drop patient records or click to upload'}
              </span>
              {documents.length > 0 && (
                <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">
                  {documents.length} document{documents.length === 1 ? '' : 's'} uploaded &middot; drop more to add
                </span>
              )}
              <span className="text-xs text-medical-500 dark:text-medical-400">
                PDF, DOCX, DOC, RTF, ODT, TXT, images, DICOM, ZIP &mdash; max 500MB per file, 1GB per case
              </span>
            </div>
          </div>

          {/* Middle: Document Directory List */}
          <div className="w-full lg:w-[45%] flex flex-col">
            <div className="flex-1 border border-medical-200 dark:border-medical-700 rounded-lg overflow-hidden bg-white dark:bg-medical-800 flex flex-col">
              {isLoading ? (
                <div className="flex items-center justify-center h-full min-h-[60px]">
                  <Loader className="h-4 w-4 animate-spin text-primary-600" />
                  <span className="ml-2 text-xs text-medical-600 dark:text-medical-400">Loading...</span>
                </div>
              ) : (error && documents.length === 0) ? (
                <div className="flex items-center justify-center h-full min-h-[60px]">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  <span className="text-xs text-red-600">Error loading documents</span>
                </div>
              ) : documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[140px] text-center px-4">
                  <FileText className="h-6 w-6 text-medical-300 dark:text-medical-600 mb-1.5" />
                  <p className="text-xs text-medical-500 dark:text-medical-400">No documents yet</p>
                  <p className="text-[11px] text-medical-400 dark:text-medical-500 mt-0.5">Upload files to get started</p>
                </div>
              ) : (
                <>
                  {/* Column headers */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 border-b border-medical-200 dark:border-medical-700 bg-medical-50 dark:bg-medical-900/40 flex-shrink-0">
                    <span className="w-4 flex-shrink-0" />
                    <button onClick={() => handleDocSort('filename')} className="flex items-center gap-0.5 flex-1 min-w-0 text-[10px] font-semibold uppercase tracking-wide text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 transition-colors text-left">
                      Name
                      {docSortField === 'filename' ? (docSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                    <button onClick={() => handleDocSort('fileSize')} className="flex items-center gap-0.5 w-[72px] flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 transition-colors text-right justify-end">
                      Size
                      {docSortField === 'fileSize' ? (docSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                    <button onClick={() => handleDocSort('pageCount')} className="flex items-center gap-0.5 w-[40px] flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 transition-colors text-right justify-end">
                      Pg
                      {docSortField === 'pageCount' ? (docSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                    <button onClick={() => handleDocSort('processingStatus')} className="flex items-center gap-0.5 w-[16px] flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 transition-colors justify-center" title="Status">
                      {docSortField === 'processingStatus' ? (docSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                    <span className="w-[40px] flex-shrink-0" />
                  </div>
                  {/* Document rows */}
                  <div className="max-h-[220px] overflow-y-auto pointer-events-auto" data-testid="documents-list">
                    {sortedDocuments.map((doc, index) => (
                      <div key={doc.documentId ?? index} className="flex items-center gap-1.5 px-2.5 py-[3px] hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors group border-b border-medical-50 dark:border-medical-700/50 last:border-b-0" data-testid="document-item" data-document-id={doc.documentId}>
                        <span className="text-sm flex-shrink-0 flex items-center justify-center w-4">{getDocumentIcon(doc)}</span>
                        <span className="flex-1 min-w-0 text-xs font-medium text-medical-900 dark:text-medical-100 truncate flex items-center gap-1.5">
                          {doc.filename}
                          {(doc.documentType === 'expert_report' || doc.documentType === 'witness_statement') && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0">Expert Evidence</span>
                          )}
                        </span>
                        <span className="w-[72px] flex-shrink-0 text-[11px] text-medical-500 dark:text-medical-400 text-right tabular-nums">{doc.fileSize ? formatFileSize(doc.fileSize) : '\u2014'}</span>
                        <span className="w-[40px] flex-shrink-0 text-[11px] text-medical-500 dark:text-medical-400 text-right tabular-nums">{doc.pageCount || '\u2014'}</span>
                        <span className="relative w-[16px] flex-shrink-0 flex justify-center group/status" data-testid="processing-status">
                          {getProcessingStatusIcon(doc.processingStatus)}
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover/status:opacity-100 pointer-events-none transition-opacity z-50">
                            {getStatusTooltip(doc.processingStatus)}
                          </span>
                        </span>
                        <div className={`flex items-center gap-0.5 w-[40px] justify-end flex-shrink-0 ${analysisInProgress ? 'pointer-events-none opacity-30' : ''}`}>
                          <button
                            onClick={() => documentApi.viewDocument(doc.documentId)}
                            className="p-0.5 text-medical-400 hover:text-primary-600 rounded transition-colors"
                            title="View document"
                            data-testid="view-document-button"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this document?')) {
                                deleteMutation.mutate(doc.documentId);
                              }
                            }}
                            className="p-0.5 text-medical-400 hover:text-red-600 rounded transition-colors"
                            title="Delete document"
                            disabled={deleteMutation.isLoading}
                            data-testid="delete-document-button"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>


          {/* Right: Analysis Trigger / Status */}
          <div className="w-full lg:w-[20%] flex flex-col flex-shrink-0">
            {hasMetadata && !analysisInProgress ? (
              /* ── Post-analysis: success state ── */
              <div className="flex-1 rounded-lg border-2 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 p-3 transition-colors flex flex-col items-center justify-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-800 dark:text-green-300">Analysis Complete</span>
                <button
                  onClick={() => navigate(`/cases/${caseId}/overview`)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
                >
                  View Results
                </button>
                <button
                  onClick={handleStartAnalysis}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  <span>Re-run Analysis</span>
                </button>
              </div>
            ) : (
              /* ── Pre-analysis / in-progress: trigger state ── */
              <div className={`flex-1 rounded-lg border-2 p-3 transition-colors flex flex-col items-center justify-center gap-3 ${
                bundleComplete && !analysisInProgress && !documentsStillProcessing
                  ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
                  : documents.length > 0 && !analysisInProgress
                    ? 'border-primary-200 bg-white dark:border-primary-800 dark:bg-medical-800'
                    : 'border-medical-200 bg-white dark:border-medical-700 dark:bg-medical-800'
              }`}>
                <label className={`flex items-center gap-2.5 select-none ${documents.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}>
                  <input
                    type="checkbox"
                    checked={bundleComplete}
                    onChange={(e) => setBundleComplete(e.target.checked)}
                    className="h-4 w-4 rounded border-medical-300 text-primary-600 focus:ring-primary-500 flex-shrink-0 disabled:opacity-40 disabled:cursor-default"
                    disabled={analysisInProgress || documents.length === 0 || documentsStillProcessing || instructionLetterItems.some(il => il.extracting)}
                  />
                  <span className={`text-xs font-medium ${documents.length > 0 ? 'text-medical-900 dark:text-medical-100' : 'text-gray-400 dark:text-medical-500'}`}>
                    My document bundle is complete
                  </span>
                </label>
                {documentsStillProcessing && !analysisInProgress && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <Loader className="h-3 w-3 animate-spin" />
                    Documents are still being saved
                  </p>
                )}
                {instructionLetterItems.some(il => il.extracting) && !analysisInProgress && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <Loader className="h-3 w-3 animate-spin" />
                    Instruction letter is being processed
                  </p>
                )}
                {!analysisInProgress && (() => {
                  const hint = documents.length === 0
                    ? 'Upload your medical records to get started.'
                    : documentsStillProcessing
                    ? 'Waiting for documents to finish processing…'
                    : instructionLetterItems.some(il => il.extracting)
                    ? 'Waiting for the instruction letter to finish processing…'
                    : effectiveFraming === null
                    ? 'Choose a framing option below — case brief or records only.'
                    : !bundleComplete
                    ? 'Tick the checkbox above to confirm your documents are ready.'
                    : null;
                  return hint ? (
                    <div className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs leading-snug">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>{hint}</span>
                    </div>
                  ) : null;
                })()}
                <button
                  onClick={handleStartAnalysis}
                  disabled={!bundleComplete || analysisInProgress || documentsStillProcessing || instructionLetterItems.some(il => il.extracting) || effectiveFraming === null}
                  className={`w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    bundleComplete && !analysisInProgress && !documentsStillProcessing && !instructionLetterItems.some(il => il.extracting) && effectiveFraming !== null
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                      : 'bg-gray-200 dark:bg-medical-700 text-gray-400 dark:text-medical-500 cursor-not-allowed'
                  }`}
                >
                  {analysisInProgress ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Running...</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>Start Analysis</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
          )}
        </div>

        {/* Upload Progress */}
        {uploadProgress.length > 0 && (() => {
          const errorFiles = uploadProgress.filter(p => p.status === 'error');
          const savingCount = uploadProgress.filter(p => p.status === 'processing').length;
          const uploadingCount = uploadProgress.filter(p => p.status === 'uploading').length;
          const avgProgress = Math.round(
            uploadProgress.reduce((sum, p) => sum + p.progress, 0) / uploadProgress.length
          );
          const isSaving = savingCount > 0 && uploadingCount === 0;

          return (
            <div className="mb-4" data-testid="upload-progress">
              {/* Only show progress bar when there are active (non-error) uploads */}
              {errorFiles.length < uploadProgress.length && (
                <div className="bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 rounded-lg p-3" data-testid="upload-progress-item">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-medical-900 dark:text-medical-100">
                      {isSaving
                        ? `Saving ${uploadProgress.length - errorFiles.length} file${uploadProgress.length - errorFiles.length !== 1 ? 's' : ''}...`
                        : `Uploading ${uploadProgress.length - errorFiles.length} file${uploadProgress.length - errorFiles.length !== 1 ? 's' : ''}...`
                      }
                    </span>
                    <span className="text-sm text-medical-600 dark:text-medical-400">
                      {isSaving ? 'Saving...' : `${avgProgress}%`}
                    </span>
                  </div>
                  <div className="bg-medical-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        isSaving ? 'bg-amber-500 animate-pulse' : 'bg-primary-600'
                      }`}
                      style={{ width: isSaving ? '100%' : `${avgProgress}%` }}
                    />
                  </div>
                </div>
              )}
              {errorFiles.length > 0 && (
                <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="px-3 py-2 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setUploadErrorsExpanded(prev => !prev)}
                      className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span className="font-medium">{errorFiles.length} file{errorFiles.length !== 1 ? 's' : ''} failed to upload</span>
                      {uploadErrorsExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadProgress(prev => prev.filter(p => p.status !== 'error'))}
                      className="p-0.5 text-red-400 hover:text-red-600 rounded transition-colors flex-shrink-0 ml-2"
                      title="Dismiss all"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {uploadErrorsExpanded && (
                    <div className="px-3 pb-2 space-y-1 border-t border-red-200 dark:border-red-800 pt-2">
                      {errorFiles.map((progress, index) => (
                        <p key={index} className="text-sm text-red-600 dark:text-red-400">
                          <span className="font-medium">{progress.filename}</span> — {progress.error || 'Upload failed'}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Document processing banner — shown when backend is still processing uploaded docs */}
        {hasProcessingDocs && !analysisInProgress && (
          <div className="mb-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg px-4 py-3 flex items-center gap-3">
            <Loader className="h-4 w-4 text-cyan-500 animate-spin flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-cyan-800 dark:text-cyan-200">
                Processing documents
              </p>
              <p className="text-xs text-cyan-600 dark:text-cyan-400">
                {processingDocCount} document{processingDocCount !== 1 ? 's are' : ' is'} being prepared — extracting text and applying OCR where needed. This usually takes a few moments.
              </p>
            </div>
          </div>
        )}

        {/* Case framing choice — deliberate, no default. Gates Start Analysis.
            Both cards stay interactive so the user can change their mind. The
            records-only card is locked if case brief data already exists, to
            prevent orphaning case type / instruction letter / dates / guidance. */}
        <div className="mb-4">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-medical-900 dark:text-medical-100">
              How should we frame this case? <span className="text-xs font-normal text-medical-500 dark:text-medical-400">(required)</span>
            </h2>
            <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">
              Choose one before starting analysis.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(() => {
              const briefSelected = effectiveFraming === 'brief';
              const recordsSelected = effectiveFraming === 'records-only';
              const recordsLocked = hasBriefData;

              const baseCard = 'text-left p-4 rounded-lg border-2 transition-colors';
              const selectedClasses = 'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20';
              const unselectedInteractive = 'bg-white dark:bg-medical-800 border-medical-200 dark:border-medical-700 hover:border-primary-400 hover:bg-primary-50/30 dark:hover:border-primary-600 dark:hover:bg-primary-900/10 cursor-pointer';
              const lockedClasses = 'bg-medical-50 dark:bg-medical-800/50 border-medical-200 dark:border-medical-700 opacity-50 cursor-not-allowed';

              return (
                <>
                  <button
                    type="button"
                    onClick={() => { setCaseFraming('brief'); setConstraintsOpen(true); }}
                    aria-pressed={briefSelected}
                    className={`${baseCard} ${briefSelected ? selectedClasses : unselectedInteractive}`}
                    data-testid="framing-brief"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Briefcase className={`w-5 h-5 flex-shrink-0 ${briefSelected ? 'text-primary-600 dark:text-primary-400' : 'text-primary-500'}`} />
                      <h3 className="text-sm font-semibold text-medical-900 dark:text-medical-100">Add a case brief</h3>
                      {briefSelected && (
                        <CheckCircle className="w-4 h-4 text-primary-600 dark:text-primary-400 ml-auto flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-medical-600 dark:text-medical-400 leading-relaxed">
                      Upload an instruction letter or claimant's statement, or set the case type, relevant dates, and any processing guidance.
                    </p>
                    <p className="text-[11px] text-primary-600 dark:text-primary-400 mt-2 font-medium">
                      Recommended for instructed work
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={recordsLocked ? undefined : () => setCaseFraming('records-only')}
                    disabled={recordsLocked}
                    aria-pressed={recordsSelected}
                    className={`${baseCard} ${recordsSelected ? selectedClasses : recordsLocked ? lockedClasses : unselectedInteractive}`}
                    data-testid="framing-records-only"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <FileText className={`w-5 h-5 flex-shrink-0 ${recordsSelected ? 'text-primary-600 dark:text-primary-400' : 'text-primary-500'}`} />
                      <h3 className="text-sm font-semibold text-medical-900 dark:text-medical-100">Records only</h3>
                      {recordsSelected && (
                        <CheckCircle className="w-4 h-4 text-primary-600 dark:text-primary-400 ml-auto flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-medical-600 dark:text-medical-400 leading-relaxed">
                      Analyse the uploaded records without additional context.
                    </p>
                    <p className="text-[11px] text-medical-500 dark:text-medical-400 mt-2 font-medium">
                      {recordsLocked ? 'Unavailable — case brief data already set' : 'Suitable for initial review or triage'}
                    </p>
                  </button>
                </>
              );
            })()}
          </div>
        </div>


        {/* Case Brief — collapsible, editable (shown when framing = brief) */}
        {effectiveFraming === 'brief' && (
        <div className="mb-4 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 rounded-lg">
          <button
            type="button"
            onClick={() => setConstraintsOpen(!constraintsOpen)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colors"
          >
            <h2 className="text-sm font-semibold text-medical-900 dark:text-medical-100 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-primary-500" />
              Case Brief
              {!constraintsOpen && selectedCaseTypes.length > 0 && (
                <>
                  {selectedCaseTypes.map(t => (
                    <span key={t} className="inline-block px-2 py-0.5 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-xs font-medium">
                      {CASE_TYPE_LABELS[t] || t}
                    </span>
                  ))}
                </>
              )}
            </h2>
            <ChevronDown className={`w-4 h-4 text-medical-400 transition-transform duration-200 ${constraintsOpen ? '' : '-rotate-90'}`} />
          </button>

          {constraintsOpen && (
            <div className="px-4 pb-4 pt-1">
              {/* Instruction Letter Zone */}
              <div className="mb-3 space-y-2">
                {/* Existing instruction letters */}
                {instructionLetterItems.map((item) => (
                  <div key={item.docId}>
                    {item.extracting ? (
                      <div className="border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-3 flex items-center gap-3">
                        <Loader className="h-4 w-4 text-cyan-600 animate-spin flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-cyan-800 dark:text-cyan-200">Extracting case context from {item.filename}...</p>
                          <p className="text-xs text-cyan-600 dark:text-cyan-400">Identifying questions, allegations, and document manifest</p>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-green-800 dark:text-green-200 truncate">
                                {item.filename || 'Instruction Letter'}
                              </span>
                            </div>
                            {item.data && (
                              <>
                                <div className="text-xs text-green-700 dark:text-green-300 space-y-0.5">
                                  {item.data.caseSummary && <p className="truncate">Case: {item.data.caseSummary}</p>}
                                  {item.data.admittedNegligence?.length > 0 && <p className="truncate">Admitted: {item.data.admittedNegligence.join('; ')}</p>}
                                  {item.data.questionsToAnswer?.length > 0 && <p>Questions: {item.data.questionsToAnswer.length} identified</p>}
                                  {item.data.documentManifest?.length > 0 && (
                                    <p>
                                      Documents listed: {item.data.documentManifest.length}
                                      {item.data.documentManifest.filter(d => d.privileged).length > 0 &&
                                        ` (${item.data.documentManifest.filter(d => d.privileged).length} privileged)`}
                                    </p>
                                  )}
                                </div>
                                {item.expanded && (
                                  <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-700 text-xs text-green-700 dark:text-green-300 space-y-2">
                                    {item.data.parties && (
                                      <div>
                                        <span className="font-medium">Parties: </span>
                                        {item.data.parties.claimant && `Claimant: ${item.data.parties.claimant}`}
                                        {item.data.parties.defendant && ` | Defendant: ${item.data.parties.defendant}`}
                                      </div>
                                    )}
                                    {item.data.reportType && <div><span className="font-medium">Report type: </span>{item.data.reportType}</div>}
                                    {item.data.questionsToAnswer?.map(q => (
                                      <div key={q.number}><span className="font-medium">Q{q.number}: </span>{q.summary}</div>
                                    ))}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setInstructionLetterItems(prev => prev.map(il => il.docId === item.docId ? { ...il, expanded: !il.expanded } : il))}
                                  className="text-xs text-green-600 dark:text-green-400 hover:underline mt-1"
                                >
                                  {item.expanded ? 'Show less' : 'View details'}
                                </button>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveInstructionLetter(item.docId)}
                            className="p-1 text-green-500 hover:text-red-500 rounded transition-colors flex-shrink-0"
                            title="Remove instruction letter"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Upload zone — only shown when no letter is uploaded */}
                {instructionLetterItems.length === 0 && (
                  <label
                    className={`border-2 border-dashed rounded-lg p-3 flex items-center gap-4 cursor-pointer transition-colors ${
                      ilDragOver
                        ? 'border-primary-500 bg-primary-100 dark:border-primary-400 dark:bg-primary-900/30'
                        : 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIlDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIlDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIlDragOver(false); }}
                    onDrop={handleIlDrop}
                  >
                    <Upload className="h-5 w-5 text-primary-400 dark:text-primary-500 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-medical-900 dark:text-medical-100">
                        {ilDragOver ? 'Drop file here' : 'Upload instruction letter or claimant\'s statement'}
                      </span>
                      <span className="text-xs text-medical-400 dark:text-medical-500 ml-1">(optional)</span>
                      <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Drag &amp; drop or click &mdash; PDF, DOCX, DOC, RTF, ODT, or TXT, max 50MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.doc,.txt,.rtf,.odt"
                      onChange={handleInstructionLetterUpload}
                    />
                  </label>
                )}
              </div>

              <div className="space-y-2.5">
                {/* Case Type row: inline label + chips + date pickers right-aligned */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-xs text-medical-500 dark:text-medical-400 flex-shrink-0">Case Type</span>
                    {Object.entries(CASE_TYPE_LABELS).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleCaseTypeToggle(value)}
                        className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                          selectedCaseTypes.includes(value)
                            ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                            : 'border-gray-200 dark:border-medical-600 text-gray-700 dark:text-medical-300 hover:bg-gray-50 dark:hover:bg-medical-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-xs text-medical-500 dark:text-medical-400">From</label>
                    <input
                      type="date"
                      value={incidentDateStart}
                      onChange={(e) => { setIncidentDateStartWrapped(e.target.value); scheduleAutoSave(); }}
                      className="input-field !py-1 text-xs w-32"
                    />
                    <label className="text-xs text-medical-500 dark:text-medical-400">To</label>
                    <input
                      type="date"
                      value={incidentDateEnd}
                      onChange={(e) => { setIncidentDateEndWrapped(e.target.value); scheduleAutoSave(); }}
                      className="input-field !py-1 text-xs w-32"
                    />
                  </div>
                </div>

                {/* Extraction Template row: inline label + chips */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-medical-500 dark:text-medical-400 flex-shrink-0">Template</span>
                  {availableTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                        selectedTemplate === template.id
                          ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                          : 'border-gray-200 dark:border-medical-600 text-gray-700 dark:text-medical-300 hover:bg-gray-50 dark:hover:bg-medical-700'
                      }`}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>

                {/* Guidance textarea */}
                <div>
                  <textarea
                    value={customGuidanceText}
                    onChange={(e) => { setCustomGuidanceText(e.target.value); stashedCustomText.current = e.target.value; setSelectedTemplate('custom'); scheduleAutoSave(); }}
                    placeholder="e.g., Extract sepsis-related events — vital signs, blood tests, and antibiotic administration timelines."
                    rows={8}
                    className="input-field resize-y text-xs"
                  />
                  <div className="mt-1 flex items-center gap-1.5">
                    <Info className="w-3 h-3 text-cyan-500 flex-shrink-0" />
                    <p className="text-[11px] text-cyan-600 dark:text-cyan-400">
                      Guides what AI extracts and prioritises. Selecting a template or uploading an instruction letter pre-fills this field.
                    </p>
                  </div>
                </div>

                {/* Clinical Guidelines */}
                <div className="pt-2.5">
                  <GuidelinePicker
                    selectedGuidelines={selectedGuidelines}
                    onGuidelinesChange={handleGuidelinesChange}
                    suggestedGuidelineIds={suggestedData?.suggestedGuidelineIds}
                    suggestedGuidelineReasons={suggestedData?.guidelines?.reduce<Record<string, string[]>>((acc, g) => {
                      acc[g.id] = g.reasons;
                      return acc;
                    }, {})}
                    onFindRelevant={handleFindRelevantGuidelines}
                    isFindingRelevant={isFindingRelevant}
                    caseId={caseId}
                    onUploadGuideline={handleUploadGuideline}
                    uploadedGuidelineFiles={uploadedGuidelineFiles}
                    onRemoveUploadedGuideline={handleRemoveUploadedGuideline}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        )}
        </div>{/* end disabled wrapper */}

      </div>
    </>
  );
};
